/** Metric names tracked by the reporter */
export type MetricName = 'LCP' | 'INP' | 'CLS';

/** Rating thresholds from web-vitals */
export type MetricRating = 'good' | 'needs-improvement' | 'poor';

/** Shape of the metric as used internally by the reporter */
export interface WebVitalMetric {
  name: MetricName;
  value: number;
  rating: MetricRating;
  attribution: Record<string, unknown>;
  route: string;
}

/** Configuration for the WebVitalsReporter */
export interface WebVitalsReporterConfig {
  endpoint: string;
  isProduction: boolean;
  maxQueueSize: number;
}

/** Full captured metric with additional metadata */
export interface CapturedMetric {
  id: string;
  name: MetricName;
  value: number;
  rating: MetricRating;
  attribution: {
    element?: string;
    url?: string;
    eventTarget?: string;
    eventType?: string;
    largestShiftTarget?: string;
    [key: string]: unknown;
  };
  route: string;
  timestamp: number;
  navigationType: string;
}

/** Internal queue state for metrics awaiting delivery */
export interface QueueState {
  entries: CapturedMetric[];
  lastFlushAttempt: number;
}

/**
 * Minimal interface for the web-vitals Metric callback payload.
 * Compatible with the web-vitals/attribution build.
 */
export interface WebVitalsMetricPayload {
  id: string;
  name: string;
  value: number;
  rating: string;
  attribution?: Record<string, unknown>;
  navigationType?: string;
}

export class WebVitalsReporter {
  private queue: QueueState;
  private config: WebVitalsReporterConfig;

  constructor(config: Partial<WebVitalsReporterConfig> & { endpoint: string }) {
    this.config = {
      endpoint: config.endpoint,
      isProduction: config.isProduction ?? true,
      maxQueueSize: config.maxQueueSize ?? 50,
    };
    this.queue = {
      entries: [],
      lastFlushAttempt: 0,
    };
  }

  /**
   * Captures a metric from the web-vitals callback and transforms it
   * into a CapturedMetric with route and timestamp information.
   */
  capture(metric: WebVitalsMetricPayload): CapturedMetric {
    const captured: CapturedMetric = {
      id: metric.id,
      name: metric.name as MetricName,
      value: metric.value,
      rating: metric.rating as MetricRating,
      attribution: this.extractAttribution(metric.attribution),
      route: typeof window !== 'undefined' ? window.location.pathname : '',
      timestamp: Date.now(),
      navigationType: metric.navigationType ?? 'navigate',
    };
    return captured;
  }

  /**
   * Reports a captured metric to the analytics endpoint (production)
   * or logs to console (non-production). Uses navigator.sendBeacon
   * or fire-and-forget fetch to avoid blocking the main thread.
   */
  async report(metric: CapturedMetric): Promise<void> {
    if (!this.config.isProduction) {
      this.logToConsole(metric);
      return;
    }

    const success = this.sendBeaconOrFetch(metric);
    if (!success) {
      this.enqueue(metric);
    }
  }

  /**
   * Flushes queued metrics on next navigation.
   * Attempts to send all queued entries; entries that fail again are discarded.
   * Never blocks the main thread for more than 50ms total.
   */
  async flush(): Promise<void> {
    if (this.queue.entries.length === 0) {
      return;
    }

    this.queue.lastFlushAttempt = Date.now();
    const entries = [...this.queue.entries];
    this.queue.entries = [];

    // Send all queued metrics via beacon (non-blocking)
    for (const entry of entries) {
      this.sendBeaconOrFetch(entry);
    }
  }

  /**
   * Returns the current queue state (for testing purposes).
   */
  getQueueState(): QueueState {
    return {
      entries: [...this.queue.entries],
      lastFlushAttempt: this.queue.lastFlushAttempt,
    };
  }

  /**
   * Queues a metric when the endpoint is unreachable.
   * Maintains FIFO eviction when queue exceeds maxQueueSize.
   */
  enqueue(metric: CapturedMetric): void {
    if (this.queue.entries.length >= this.config.maxQueueSize) {
      // FIFO eviction: remove the oldest entry
      this.queue.entries.shift();
    }
    this.queue.entries.push(metric);
  }

  /**
   * Attempts to send a metric using navigator.sendBeacon (preferred)
   * or a fire-and-forget fetch call. Returns true if the send was
   * initiated without error, false if the endpoint is unreachable.
   */
  private sendBeaconOrFetch(metric: CapturedMetric): boolean {
    const payload = JSON.stringify(metric);

    try {
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const sent = navigator.sendBeacon(this.config.endpoint, payload);
        return sent;
      }

      // Fallback: fire-and-forget fetch (non-blocking)
      if (typeof fetch !== 'undefined') {
        fetch(this.config.endpoint, {
          method: 'POST',
          body: payload,
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
        }).catch(() => {
          // Silently ignore — fire-and-forget
        });
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  /**
   * Logs the metric to the browser console in non-production environments.
   */
  private logToConsole(metric: CapturedMetric): void {
    const label = `[Web Vitals] ${metric.name}`;
    const details = {
      value: metric.value,
      rating: metric.rating,
      route: metric.route,
      attribution: metric.attribution,
    };

    if (metric.rating === 'poor') {
      console.warn(label, details);
    } else {
      console.log(label, details);
    }
  }

  /**
   * Extracts attribution data from the web-vitals attribution object,
   * mapping known fields to our CapturedMetric attribution shape.
   */
  private extractAttribution(
    attribution?: Record<string, unknown>
  ): CapturedMetric['attribution'] {
    if (!attribution) {
      return {};
    }

    return {
      element: attribution.element as string | undefined,
      url: attribution.url as string | undefined,
      eventTarget: attribution.eventTarget as string | undefined,
      eventType: attribution.eventType as string | undefined,
      largestShiftTarget: attribution.largestShiftTarget as string | undefined,
      ...attribution,
    };
  }
}
